import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  
  // Intersection observers for scroll animations
  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ctaRef, ctaInView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSection
        ref={heroRef}
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeIn}
      >
        <HeroContent>
          <HeroText>
            <HeroTitle>{t('home.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('home.hero.subtitle')}</HeroSubtitle>
            <HeroButtons>
              <PrimaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://app.xenya-face.app"
              >
                {t('home.cta.getStarted')}
              </PrimaryButton>
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#features"
              >
                {t('home.cta.learnMore')}
              </SecondaryButton>
            </HeroButtons>
          </HeroText>
          <HeroImageContainer>
            <HeroImage src="/assets/profile/img-card.jpg" alt="AI Skin Analysis" />
            <HeroImageOverlay />
          </HeroImageContainer>
        </HeroContent>
      </HeroSection>
      
      {/* Features Section */}
      <FeaturesSection
        id="features"
        ref={featuresRef}
      >
        <SectionTitle>{t('home.sectionTitles.features')}</SectionTitle>
        <FeaturesContainer>
          <FeatureCard
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <FeatureIcon>🔍</FeatureIcon>
            <FeatureTitle>{t('home.features.ai.title')}</FeatureTitle>
            <FeatureDescription>{t('home.features.ai.description')}</FeatureDescription>
          </FeatureCard>
          
          <FeatureCard
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FeatureIcon>💬</FeatureIcon>
            <FeatureTitle>{t('home.features.consultation.title')}</FeatureTitle>
            <FeatureDescription>{t('home.features.consultation.description')}</FeatureDescription>
          </FeatureCard>
          
          <FeatureCard
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <FeatureIcon>📊</FeatureIcon>
            <FeatureTitle>{t('home.features.progress.title')}</FeatureTitle>
            <FeatureDescription>{t('home.features.progress.description')}</FeatureDescription>
          </FeatureCard>
        </FeaturesContainer>
      </FeaturesSection>
      
      {/* How It Works Section */}
      <HowItWorksSection>
        <SectionTitle>{t('home.sectionTitles.howItWorks')}</SectionTitle>
        <StepsContainer>
          <Step>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>{t('home.howItWorks.step1.title')}</StepTitle>
              <StepDescription>
                {t('home.howItWorks.step1.description')}
              </StepDescription>
            </StepContent>
          </Step>
          
          <StepDivider />
          
          <Step>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>{t('home.howItWorks.step2.title')}</StepTitle>
              <StepDescription>
                {t('home.howItWorks.step2.description')}
              </StepDescription>
            </StepContent>
          </Step>
          
          <StepDivider />
          
          <Step>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>{t('home.howItWorks.step3.title')}</StepTitle>
              <StepDescription>
                {t('home.howItWorks.step3.description')}
              </StepDescription>
            </StepContent>
          </Step>
        </StepsContainer>
      </HowItWorksSection>
      
      {/* Testimonials Section */}
      <TestimonialsSection>
        <SectionTitle>{t('home.sectionTitles.testimonials')}</SectionTitle>
        <TestimonialsContainer>
          <Testimonial>
            <TestimonialContent>
              {t('home.testimonials.testimonial1.content')}
            </TestimonialContent>
            <TestimonialAuthor>
              <AuthorImage src="/assets/profile/femme-1.jpg" alt="User" />
              <AuthorInfo>
                <AuthorName>{t('home.testimonials.testimonial1.author')}</AuthorName>
                <AuthorTitle>{t('home.testimonials.testimonial1.title')}</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </Testimonial>
          
          <Testimonial>
            <TestimonialContent>
              {t('home.testimonials.testimonial2.content')}
            </TestimonialContent>
            <TestimonialAuthor>
              <AuthorImage src="/assets/profile/homme-1.jpg" alt="User" />
              <AuthorInfo>
                <AuthorName>{t('home.testimonials.testimonial2.author')}</AuthorName>
                <AuthorTitle>{t('home.testimonials.testimonial2.title')}</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </Testimonial>
          
          <Testimonial>
            <TestimonialContent>
              {t('home.testimonials.testimonial3.content')}
            </TestimonialContent>
            <TestimonialAuthor>
              <AuthorImage src="/assets/profile/femme-2.jpg" alt="User" />
              <AuthorInfo>
                <AuthorName>{t('home.testimonials.testimonial3.author')}</AuthorName>
                <AuthorTitle>{t('home.testimonials.testimonial3.title')}</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </Testimonial>
        </TestimonialsContainer>
      </TestimonialsSection>
      
      {/* CTA Section */}
      <CTASection
        ref={ctaRef}
        initial="hidden"
        animate={ctaInView ? "visible" : "hidden"}
        variants={fadeIn}
      >
        <CTAContent>
          <CTATitle>{t('home.sectionTitles.cta')}</CTATitle>
          <CTADescription>
            {t('home.ctaSection.description')}
          </CTADescription>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="https://app.xenya-face.app"
          >
            {t('home.ctaSection.button')}
          </CTAButton>
        </CTAContent>
      </CTASection>
    </HomeContainer>
  );
};

// Styled Components
const HomeContainer = styled.div`
  padding-top: 60px;
`;

const HeroSection = styled(motion.section)`
  min-height: calc(100vh - 60px);
  display: flex;
  align-items: center;
  position: relative;
  padding: 80px 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 60px 0;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroText = styled.div`
  flex: 1;
  max-width: 600px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-bottom: 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 30px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    justify-content: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.gradientSecondary};
  color: ${({ theme }) => theme.colors.light};
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-decoration: none;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
`;

const SecondaryButton = styled(motion.a)`
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.light};
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
`;

const HeroImageContainer = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const HeroImage = styled.img`
  width: 100%;
  display: block;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const HeroImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(26, 26, 46, 0.8));
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    margin-bottom: 40px;
  }
`;

const FeaturesSection = styled.section`
  padding: 100px 0;
  background: rgba(26, 26, 46, 0.3);
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 20px;
  }
`;

const FeatureCard = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 100%;
  }
`;

const FeatureIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 15px;
`;

const FeatureDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
  line-height: 1.6;
`;

const HowItWorksSection = styled.section`
  padding: 100px 0;
`;

const StepsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradientSecondary};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-right: 20px;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 5px;
`;

const StepDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
`;

const StepDivider = styled.div`
  width: 100px;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 2px;
    height: 40px;
  }
`;

const TestimonialsSection = styled.section`
  padding: 100px 0;
  background: rgba(26, 26, 46, 0.3);
`;

const TestimonialsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 20px;
  }
`;

const Testimonial = styled.div`
  flex: 1;
  min-width: 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 100%;
  }
`;

const TestimonialContent = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 20px;
  position: relative;
  
  &:before, &:after {
    font-size: 40px;
    opacity: 0.3;
    position: absolute;
  }
  
  &:before {
    content: '"';
    top: -20px;
    left: -10px;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.md};
`;

const AuthorTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray};
`;

const CTASection = styled(motion.section)`
  padding: 100px 0;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.secondary};
  padding: 15px 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  text-decoration: none;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: pointer;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px 30px;
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

export default HomePage;